import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n, BRAND, LANGUAGES } from "@/i18n";

export default function Nav() {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = `/#${id}`;
  };

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const langTrigger = (
    <button
      data-testid="lang-dropdown-trigger"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border transition text-xs tracking-widest uppercase ${
        scrolled
          ? "border-[#2C2520]/20 text-[#2C2520] hover:border-[#C85A3C]"
          : "border-[#FAF9F6]/30 text-[#FAF9F6] hover:border-[#C85A3C]"
      }`}
      aria-label="language"
    >
      <span className="text-base leading-none" aria-hidden="true">{current.flag}</span>
      <span className="font-semibold">{current.code.toUpperCase()}</span>
      <ChevronDown className="w-3 h-3 opacity-70" />
    </button>
  );

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#FAF9F6]/90 backdrop-blur-md border-b border-[#E5E0D8]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 min-w-0">
          <span
            className={`font-serif-display text-xl sm:text-2xl truncate ${
              scrolled ? "text-[#2C2520]" : "text-[#FAF9F6]"
            }`}
          >
            {BRAND.name}
          </span>
        </Link>

        <div className={`hidden md:flex items-center gap-8 lg:gap-10 text-sm ${scrolled ? "text-[#2C2520]" : "text-[#FAF9F6]"}`}>
          <button onClick={() => scrollTo("classes")} data-testid="nav-classes" className="hover:text-[#C85A3C] transition">{t.nav.classes}</button>
          <Link to="/build-your-evening" data-testid="nav-bye" onClick={() => setOpen(false)} className="hover:text-[#C85A3C] transition">{t.classes.buildEvening.navLink}</Link>
          <button onClick={() => scrollTo("about")} data-testid="nav-about" className="hover:text-[#C85A3C] transition">{t.nav.about}</button>
          <button onClick={() => scrollTo("experience")} data-testid="nav-experience" className="hover:text-[#C85A3C] transition">{t.nav.experience}</button>
          <button onClick={() => scrollTo("gallery")} data-testid="nav-gallery" className="hover:text-[#C85A3C] transition">{t.nav.gallery}</button>
          <button onClick={() => scrollTo("testimonials")} data-testid="nav-testimonials" className="hover:text-[#C85A3C] transition">{t.nav.testimonials}</button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{langTrigger}</DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#FAF9F6] border border-[#E5E0D8] rounded-sm min-w-[160px] p-1 shadow-xl"
              data-testid="lang-dropdown-content"
            >
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`flex items-center gap-3 cursor-pointer rounded-sm px-3 py-2.5 text-sm transition ${
                    lang === l.code
                      ? "bg-[#C85A3C]/10 text-[#C85A3C] font-semibold"
                      : "text-[#2C2520] hover:bg-[#F2EFEB]"
                  }`}
                >
                  <span className="text-lg leading-none" aria-hidden="true">{l.flag}</span>
                  <span className="flex-1">{l.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => scrollTo("booking")}
            data-testid="nav-book-btn"
            className="btn-terracotta hidden sm:inline-flex px-4 lg:px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm"
          >
            {t.nav.book}
          </button>

          <button
            className={`md:hidden p-2 ${scrolled ? "text-[#2C2520]" : "text-[#FAF9F6]"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="menu"
          >
            <span className="block w-6 h-0.5 bg-current mb-1.5" />
            <span className="block w-6 h-0.5 bg-current mb-1.5" />
            <span className="block w-4 h-0.5 bg-current ml-auto" />
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#FAF9F6] border-t border-[#E5E0D8] px-6 py-6 flex flex-col gap-4 text-[#2C2520]">
          <button onClick={() => scrollTo("classes")} className="text-left">{t.nav.classes}</button>
          <Link to="/build-your-evening" onClick={() => setOpen(false)} data-testid="nav-bye-mobile" className="text-left text-[#C85A3C]">{t.classes.buildEvening.navLink}</Link>
          <button onClick={() => scrollTo("about")} className="text-left">{t.nav.about}</button>
          <button onClick={() => scrollTo("experience")} className="text-left">{t.nav.experience}</button>
          <button onClick={() => scrollTo("gallery")} className="text-left">{t.nav.gallery}</button>
          <button onClick={() => scrollTo("testimonials")} className="text-left">{t.nav.testimonials}</button>
          <button onClick={() => scrollTo("booking")} data-testid="nav-book-mobile" className="btn-terracotta py-3 mt-2 rounded-sm tracking-widest uppercase text-xs">{t.nav.book}</button>
        </div>
      )}
    </nav>
  );
}
